import React, { useState, useEffect, useRef } from "react";
import { api } from "../features/apiServices/apiClient";
import { useNavigate } from "react-router-dom";
import ModalMesaClick from "../components/ModalMesaClick";
import safeJSON from "../components/safeJSON";
import { RelatorioPDF } from "../reports/RelatorioPDF";
import { gerarRelatorioPDF } from "../reports/gerarPDF";
import "./css/RelatoriosPage.css";


const RelatoriosPage = () => {
    const navigate = useNavigate();

    const token = localStorage.getItem("accessToken");
    const tenant = safeJSON("tenant");
    const user = safeJSON("user");
    const activeRestaurant = safeJSON("activeRestaurant");

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState("");

    const isOwner = user?.role === "owner" || user?.is_owner === true;
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [allOrders, setAllOrders] = useState([]);
    const [originalOrders, setOriginalOrders] = useState([]);
    const [tablesData, setTablesData] = useState([]);
    const [isFiltering, setIsFiltering] = useState(false);
    const isFilteringRef = useRef(false);

    useEffect(() => {
        isFilteringRef.current = isFiltering;
    }, [isFiltering]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Filtros
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [status, setStatus] = useState("");
    const [mesa, setMesa] = useState("");
    const [mesaStatsFisicas, setMesaStatsFisicas] = useState([]);
    const [mesaStatsDelivery, setMesaStatsDelivery] = useState([]);


    // Dados dos relatórios
    const [kpis, setKpis] = useState({
        totalPedidos: 0,
        pedidosAbertos: 0,
        pedidosConcluidos: 0,
        ticketMedio: 0,
        faturamentoTotal: 0,
        mesaMaisAtiva: "--",
        horaPico: "--",
    });

    const [orders, setOrders] = useState([]);
    const [ordersFisicos, setOrdersFisicos] = useState([]);
    const [ordersDelivery, setOrdersDelivery] = useState([]);
    const [topItems, setTopItems] = useState([]);
    const [mesaStats, setMesaStats] = useState([]);
    const [fetchDone, setFetchDone] = useState(false);


    useEffect(() => {
        if (!token || !tenant || !user) {
            navigate("/Login");
            return;
        }

        if (!activeRestaurant) return;
        if (fetchDone) return;   // <-- evita chamadas repetidas

        fetchReports();
        setFetchDone(true);      // <-- garante que nunca mais será chamado
    }, [token, tenant, user, activeRestaurant]);


    const buildQueryParams = () => {
        const params = new URLSearchParams();
        if (activeRestaurant?.id) params.append("restaurant_id", activeRestaurant.id);
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        if (status) params.append("status", status);
        if (mesa) params.append("mesa", mesa);
        return params.toString();
    };

    const formatDateTime = (isoString) => {
        if (!isoString || isoString === "--") return "--";

        const date = new Date(isoString);
        if (isNaN(date.getTime())) return "--"; // <-- prevenção de NaN

        const pad = (n) => n.toString().padStart(2, "0");

        const dia = pad(date.getDate());
        const mes = pad(date.getMonth() + 1);
        const ano = date.getFullYear();

        const horas = pad(date.getHours());
        const minutos = pad(date.getMinutes());
        const segundos = pad(date.getSeconds());

        return `${dia}/${mes}/${ano} ${horas}:${minutos}:${segundos}`;
    };


    const fetchReports = async (isAutoRefresh = false) => {
        if (!activeRestaurant?.id) return;

        // Só mostrar loading na primeira vez, NÃO durante auto-refresh.
        if (!isAutoRefresh) {
            setLoading(true);
            setError("");
        }

        try {
            const headers = {
                Authorization: `Bearer ${token}`,
                "x-tenant-slug": tenant.slug,
            };

            // MESAS
            const respMesas = await api.get(`/tables?restaurant_id=${activeRestaurant.id}`, { headers });
            let mesas = (respMesas.data || []).sort((a, b) => {
                const numA = parseInt(a.name.replace(/\D+/g, ""));
                const numB = parseInt(b.name.replace(/\D+/g, ""));
                return numA - numB;
            });

            // COMANDAS
            const respComandas = await api.get(`/comandas/restaurant/${activeRestaurant.id}`, { headers });
            const comandas = respComandas.data || [];

            let pedidosColetados = [];

            // PEDIDOS já incluídos na resposta das comandas (elimina N+1 queries)
            for (const comanda of comandas) {
                const pedidos = comanda.pedidos || [];

                pedidos.forEach(p => {
                    const nome = p.Menu?.nome || "";
                    const preco = p.Menu?.preco || 0;
                    const quantidade = p.quantidade || 0;
                    const total = preco * quantidade;

                    pedidosColetados.push({
                        ...p,
                        pedido_id: p.id,
                        comanda_id: comanda.comanda_id,
                        mesa: comanda.mesa,
                        itens_resumo: `${nome} x${quantidade}`,
                        valor_total: total,
                        aberto_em: comanda.data_abertura,
                        fechado_em: comanda.data_fechamento
                    });
                });
            }

            // SEPARAR DELIVERY DAS MESAS FÍSICAS
            const pedidosDelivery = pedidosColetados.filter(p =>
                p.mesa?.toLowerCase().startsWith("delivery")
            );

            const pedidosFisicos = pedidosColetados.filter(p =>
                !p.mesa?.toLowerCase().startsWith("delivery")
            );


            setAllOrders(pedidosColetados);
            setOriginalOrders(pedidosColetados);
            setTablesData(mesas);

            // During auto-refresh, keep the user's filtered view intact
            if (!isAutoRefresh || !isFilteringRef.current) {
                setOrdersFisicos(pedidosFisicos);
                setOrdersDelivery(pedidosDelivery);
                calcularKPIs(pedidosColetados);
                calcularItensMaisVendidos(pedidosColetados);
                calcularEstatisticasMesa(pedidosColetados, mesas);
            }


        } catch (err) {
            console.error(err);
            setError("Erro ao carregar relatórios.");
        } finally {
            if (!isAutoRefresh) {
                setLoading(false);
                setIsInitialLoad(false);
            }
        }
    };




    const handleApplyFilters = (e) => {
        e.preventDefault();

        const pedidosFiltrados = originalOrders.filter((p) => {
            const dataAbertura = new Date(p.aberto_em);
            const start = startDate ? new Date(startDate + "T00:00:00") : null;
            const end = endDate ? new Date(endDate + "T23:59:59") : null;

            const byDate =
                (!start || dataAbertura >= start) &&
                (!end || dataAbertura <= end);

            const byStatus =
                !status || status === "" || p.status?.toLowerCase() === status.toLowerCase();

            const byMesa =
                !mesa ||
                mesa.trim() === "" ||
                p.mesa?.toLowerCase().includes(mesa.toLowerCase());

            return byDate && byStatus && byMesa;
        });

        const pedidosDelivery = pedidosFiltrados.filter((p) =>
            p.mesa?.toLowerCase().startsWith("delivery")
        );
        const pedidosFisicos = pedidosFiltrados.filter((p) =>
            !p.mesa?.toLowerCase().startsWith("delivery")
        );

        setIsFiltering(true);
        setOrdersDelivery(pedidosDelivery);
        setOrdersFisicos(pedidosFisicos);
        calcularEstatisticasMesa(pedidosFiltrados, tablesData);
        calcularKPIs(pedidosFiltrados);
        calcularItensMaisVendidos(pedidosFiltrados);
    };

    const handleClearFilters = () => {
        setStartDate("");
        setEndDate("");
        setStatus("");
        setMesa("");
        setIsFiltering(false);

        const pedidosDelivery = originalOrders.filter((p) =>
            p.mesa?.toLowerCase().startsWith("delivery")
        );
        const pedidosFisicos = originalOrders.filter((p) =>
            !p.mesa?.toLowerCase().startsWith("delivery")
        );

        setOrdersDelivery(pedidosDelivery);
        setOrdersFisicos(pedidosFisicos);
        calcularEstatisticasMesa(originalOrders, tablesData);
        calcularKPIs(originalOrders);
        calcularItensMaisVendidos(originalOrders);
    };


    function calcularMesaMaisAtiva(pedidos) {
        const contador = {};

        pedidos.forEach(p => {
            const mesa = p.mesa || p.mesa_nome;
            if (!contador[mesa]) contador[mesa] = 0;
            contador[mesa]++;
        });

        const mesas = Object.entries(contador);
        if (mesas.length === 0) return "--";

        mesas.sort((a, b) => b[1] - a[1]); // maior primeiro
        return mesas[0][0];                       // nome da mesa
    }


    const calcularKPIs = (pedidos) => {

        const mesamaisativa = calcularMesaMaisAtiva(pedidos);

        const total = pedidos.length;
        const concluidos = pedidos.filter(p => p.status === 'finalizado' || p.status === 'entregue').length;
        const abertos = pedidos.filter(p => p.status === 'aberto' || p.status === 'preparando' || p.status === 'pronto').length;

        const totalFaturamento = pedidos.reduce((acc, p) =>
            acc + Number(p.valor_total || 0), 0
        );

        const ticketMedio = pedidos.length > 0 ? totalFaturamento / pedidos.length : 0;

        setKpis({
            totalPedidos: total,
            pedidosAbertos: abertos,
            pedidosConcluidos: concluidos,
            faturamentoTotal: totalFaturamento,
            ticketMedio: ticketMedio,
            mesaMaisAtiva: mesamaisativa || "--",
            horaPico: "--",
        });
    };

    const calcularItensMaisVendidos = (pedidos) => {
        const mapa = {};

        pedidos.forEach(p => {
            if (!p.Menu) return;

            const nome = p.Menu.nome;
            const preco = p.Menu.preco || 0;
            const quantidade = p.quantidade || 0;
            const total = quantidade * preco;

            if (!mapa[nome]) {
                mapa[nome] = {
                    nome,
                    quantidade: 0,
                    faturamento: 0,
                    ticket_medio: 0,

                };
            }

            mapa[nome].quantidade += quantidade;
            mapa[nome].faturamento += total;
        });

        const lista = Object.values(mapa).map(item => ({
            ...item,
            ticket_medio:
                item.quantidade > 0 ? item.faturamento / item.quantidade : 0
        }));


        const listaOrdenada = lista.sort((a, b) => b.quantidade - a.quantidade);

        setTopItems(listaOrdenada);

    };

    const calcularEstatisticasMesa = (pedidos, mesas) => {

        const mesasFisicas = mesas.filter(
            m => !m.name.toLowerCase().startsWith("delivery")
        );

        const mesasDelivery = mesas.filter(
            m => m.name.toLowerCase().startsWith("delivery")
        );

        // Mesa física
        const statsFisicas = mesasFisicas.map(m => {
            const pedidosMesa = pedidos.filter(p => p.mesa === m.name);

            const qtd = pedidosMesa.length;
            const total = pedidosMesa.reduce((acc, p) => acc + Number(p.valor_total || 0), 0);
            const media = qtd > 0 ? total / qtd : 0;

            return {
                mesa_nome: m.name,
                qtd_pedidos: qtd,
                valor_total: total,
                media_por_pedido: media,
            };
        });

        // Delivery
        const statsDelivery = mesasDelivery.map(m => {
            const pedidosMesa = pedidos.filter(p => p.mesa === m.name);

            const qtd = pedidosMesa.length;
            const total = pedidosMesa.reduce((acc, p) => acc + Number(p.valor_total || 0), 0);
            const media = qtd > 0 ? total / qtd : 0;

            return {
                mesa_nome: m.name,
                qtd_pedidos: qtd,
                valor_total: total,
                media_por_pedido: media,
            };
        });

        setMesaStatsFisicas(statsFisicas);
        setMesaStatsDelivery(statsDelivery);
    };


    useEffect(() => {
        const interval = setInterval(() => {
            if (!isFilteringRef.current) {
                fetchReports(true);
            }
        }, 5000);

        return () => clearInterval(interval);
    }, []);




    const exportarCSV = () => {
        let csv = "";

        // ========== KPIs ==========
        csv += "KPIs\n";
        csv += `Total de pedidos;${kpis.totalPedidos}\n`;
        csv += `Pedidos abertos;${kpis.pedidosAbertos}\n`;
        csv += `Pedidos concluídos;${kpis.pedidosConcluidos}\n`;
        csv += `Ticket médio;${kpis.ticketMedio.toFixed(2)}\n`;
        csv += `Faturamento total;${kpis.faturamentoTotal.toFixed(2)}\n\n`;

        // ========== PEDIDOS ==========
        csv += "Pedidos no período\n";
        csv += "Pedido;Mesa;Itens;Total;Status;Aberto em;Fechado em\n";

        orders.forEach(o => {
            csv += `${o.pedido_id};${o.mesa};${o.itens_resumo};${o.valor_total};${o.status};${o.aberto_em};${o.fechado_em}\n`;
        });

        csv += "\n";

        // ========== ITENS MAIS VENDIDOS ==========
        csv += "Itens mais vendidos\n";
        csv += "Item;Quantidade;Faturamento\n";

        topItems.forEach(item => {
            csv += `${item.nome};${item.quantidade};${item.faturamento}\n`;
        });

        csv += "\n";

        // ========== DESEMPENHO POR MESA ==========
        csv += "Desempenho por mesa\n";
        csv += "Mesa;Qtd pedidos;Valor total;Média por pedido\n";



        mesaStats.forEach(m => {
            csv += `${m.mesa};${m.qtd_pedidos};${m.valor_total};${m.media_por_pedido}\n`;
        });

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", "relatorio_mesaClick.csv");
        link.click();
    };




    // Se não for owner, bloqueia a visualização
    if (!isOwner) {
        return (
            <div className="page-container-rp">
                <header className="header-rp">
                    <img
                        src="/assets/logo-mesaclick.png"
                        alt="MesaClick"
                        className="header-logo-rp"
                        onClick={() => navigate("/Home")}
                    />
                    <h1 className="header-title">Relatórios do Restaurante - {activeRestaurant.nome}</h1>
                </header>

                <div className="reports-access-denied">
                    <h2>Acesso não autorizado</h2>
                    <p>Somente o gerente pode visualizar esta página.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container-rp">
            {/* HEADER */}

            <header className="header-rp">
                <div className="relatorios-header-left">
                    <img src="/assets/logo-mesaclick.png" className="header-logo-rp" onClick={() => navigate("/Home")} />
                    <h1 className="header-title">Relatórios do Restaurante - {activeRestaurant.nome}</h1>
                </div>

                <div className="relatorios-header-right">
                    <button className="btn-export" onClick={exportarCSV}>Exportar CSV</button>
                    <button className="btn-export" onClick={gerarRelatorioPDF}>Exportar PDF</button>
                </div>
            </header>



            {/* CONTEÚDO PRINCIPAL */}
            <div className="reports-content">
                {/* PAINEL DE FILTROS */}
                <aside className="filters-panel">
                    <h2 className="panel-title">Filtros</h2>
                    <form onSubmit={handleApplyFilters} className="filters-form">
                        <div className="form-group">
                            <label>Data inicial</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label>Data final</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label>Status do pedido</label>
                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value="">Todos</option>
                                <option value="pendente">Pendente</option>
                                <option value="preparando">Preparando</option>
                                <option value="pronto">Pronto</option>
                                <option value="finalizado">Finalizado</option>
                                <option value="entregue">Entregue</option>
                                <option value="cancelado">Cancelado</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Mesa</label>
                            <input
                                type="text"
                                placeholder="Ex: Mesa 1"
                                value={mesa}
                                onChange={(e) => setMesa(e.target.value)}
                            />
                        </div>

                        <button type="submit" className="btn-apply">
                            Aplicar filtros
                        </button>
                        {isFiltering && (
                            <button type="button" className="btn-apply" style={{ marginTop: 8, background: '#6b7280' }} onClick={handleClearFilters}>
                                Limpar filtros
                            </button>
                        )}
                    </form>
                </aside>

                {/* PAINEL PRINCIPAL */}
                <main className="reports-main" id="reports-main">
                    {/* KPIs */}
                    <section className="kpi-section">
                        <div className="kpi-card">
                            <span className="kpi-label">Total de pedidos</span>
                            <span className="kpi-value">{kpis.totalPedidos}</span>
                        </div>
                        <div className="kpi-card">
                            <span className="kpi-label">Pedidos em aberto</span>
                            <span className="kpi-value">{kpis.pedidosAbertos}</span>
                        </div>
                        <div className="kpi-card">
                            <span className="kpi-label">Pedidos concluídos</span>
                            <span className="kpi-value">{kpis.pedidosConcluidos}</span>
                        </div>
                        <div className="kpi-card">
                            <span className="kpi-label">Ticket médio</span>
                            <span className="kpi-value">
                                R$ {kpis.ticketMedio.toFixed(2)}
                            </span>
                        </div>
                        <div className="kpi-card">
                            <span className="kpi-label">Faturamento total</span>
                            <span className="kpi-value">
                                R$ {kpis.faturamentoTotal.toFixed(2)}
                            </span>
                        </div>
                        <div className="kpi-card">
                            <span className="kpi-label">Mesa mais ativa</span>
                            <span className="kpi-value">{kpis.mesaMaisAtiva}</span>
                        </div>
                        <div className="kpi-card">
                            <span className="kpi-label">Hora de pico</span>
                            <span className="kpi-value">{kpis.horaPico}</span>
                        </div>
                    </section>

                    {/* ERRO / LOADING */}
                    {error && <div className="reports-error">{error}</div>}
                    {loading && <div className="reports-loading">Carregando dados...</div>}

                    {/* RELATÓRIO DE PEDIDOS */}
                    <section className="section-card">
                        <div className="section-header">
                            <h2>Pedidos (Salão)</h2>

                        </div>

                        {ordersFisicos.length === 0 ? (
                            <p className="empty-text">Nenhum pedido encontrado.</p>
                        ) : (
                            <div className="table-wrapper">
                                <table className="reports-table">
                                    <thead>
                                        <tr>
                                            <th># Pedido</th>
                                            <th>Mesa</th>
                                            <th>Itens</th>
                                            <th>Valor total</th>
                                            <th>Status</th>
                                            <th>Aberto em</th>
                                            <th>Fechado em</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ordersFisicos.map((order) => (
                                            <tr key={order.id}>
                                                <td>{(order.numero || order.id || "").slice(-5)}</td>                                                <td>{order.mesa_nome || order.mesa || "--"}</td>
                                                <td>
                                                    {order.itens_resumo ||
                                                        (order.items
                                                            ? order.items
                                                                .map((i) => `${i.nome} x${i.quantidade}`)
                                                                .join(", ")
                                                            : "--")}
                                                </td>
                                                <td>R$ {Number(order.valor_total || 0).toFixed(2)}</td>
                                                <td>
                                                    <span className={`status-badge status-${order.status?.toLowerCase()}`}>
                                                        {order.status}
                                                    </span>
                                                </td>
                                                <td>{order.aberto_em ? formatDateTime(order.aberto_em) : "--"}</td>
                                                <td>{order.fechado_em ? formatDateTime(order.fechado_em) : "--"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    <section className="section-card">
                        <div className="section-header">
                            <h2>Pedidos Delivery</h2>
                        </div>

                        {ordersDelivery.length === 0 ? (
                            <p className="empty-text">Nenhum delivery no período.</p>
                        ) : (
                            <div className="table-wrapper">
                                <table className="reports-table">
                                    <thead>
                                        <tr>
                                            <th># Pedido</th>
                                            <th>Mesa</th>
                                            <th>Itens</th>
                                            <th>Valor total</th>
                                            <th>Status</th>
                                            <th>Aberto em</th>
                                            <th>Fechado em</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ordersDelivery.map((order) => (
                                            <tr key={order.id}>
                                                <td>{(order.numero || order.id || "").slice(-5)}</td>
                                                <td>{order.mesa}</td>
                                                <td>{order.itens_resumo}</td>
                                                <td>R$ {Number(order.valor_total).toFixed(2)}</td>
                                                <td>{order.status}</td>
                                                <td>{formatDateTime(order.aberto_em)}</td>
                                                <td>{formatDateTime(order.fechado_em)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>


                    {/* ITENS MAIS VENDIDOS */}
                    <section className="section-card">
                        <div className="section-header">
                            <h2>Itens mais vendidos</h2>
                        </div>
                        {topItems.length === 0 ? (
                            <p className="empty-text">Nenhum dado de itens vendido.</p>
                        ) : (
                            <div className="table-wrapper">
                                <table className="reports-table">
                                    <thead>
                                        <tr>
                                            <th>Produto</th>
                                            <th>Quantidade</th>
                                            <th>Faturamento</th>
                                            <th>Ticket médio</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topItems.map((item) => (
                                            <tr key={item.id || item.nome}>
                                                <td>{item.nome}</td>
                                                <td>{item.quantidade}</td>
                                                <td>R$ {Number(item.faturamento || 0).toFixed(2)}</td>
                                                <td>R$ {Number(item.ticket_medio || 0).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    {/* ESTATÍSTICAS POR MESA */}
                    <section className="section-card">
                        <div className="section-header">
                            <h2>Desempenho por mesa (Salão)</h2>
                        </div>
                        {mesaStatsFisicas.length === 0 ? (
                            <p className="empty-text">Nenhuma estatística por mesa.</p>
                        ) : (
                            <div className="table-wrapper">
                                <table className="reports-table">
                                    <thead>
                                        <tr>
                                            <th>Mesa</th>
                                            <th>Qtd. pedidos</th>
                                            <th>Valor total</th>
                                            <th>Média por pedido</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mesaStatsFisicas.map((m) => (
                                            <tr key={m.mesa || m.mesa_nome}>
                                                <td>{m.mesa_nome || m.mesa}</td>
                                                <td>{m.qtd_pedidos}</td>
                                                <td>R$ {Number(m.valor_total || 0).toFixed(2)}</td>
                                                <td>R$ {Number(m.media_por_pedido || 0).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    <section className="section-card">
                        <div className="section-header">
                            <h2>Desempenho Delivery</h2>
                        </div>

                        {mesaStatsDelivery.length === 0 ? (
                            <p className="empty-text">Nenhum delivery no período.</p>
                        ) : (
                            <div className="table-wrapper">
                                <table className="reports-table">
                                    <thead>
                                        <tr>
                                            <th>Mesa</th>
                                            <th>Qtd. pedidos</th>
                                            <th>Valor total</th>
                                            <th>Média por pedido</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mesaStatsDelivery.map((m) => (
                                            <tr key={m.mesa_nome}>
                                                <td>{m.mesa_nome}</td>
                                                <td>{m.qtd_pedidos}</td>
                                                <td>R$ {Number(m.valor_total).toFixed(2)}</td>
                                                <td>R$ {Number(m.media_por_pedido).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                </main>


            </div>

            <div style={{ position: "absolute", top: "-20000px", left: "-20000px" }}>
                <RelatorioPDF
                    restaurante={activeRestaurant}
                    resumo={kpis}
                    pedidos={[...ordersFisicos, ...ordersDelivery]}
                    itens={topItems}
                    desempenho={mesaStatsFisicas}
                    delivery={mesaStatsDelivery}

                />
            </div>

            <ModalMesaClick
                open={modalOpen}
                message={modalMessage}
                onClose={() => setModalOpen(false)}
            />

        </div>
    );
};

export default RelatoriosPage;
